import { useCallback, useState } from "react";
import { ButtonGroup } from "~/components/ButtonGroup";
import { AssetPerformanceLeaderboard } from "~/modules/asset_screener/pages/listing/AssetClassReturnLeaderboard";
import {
    AssetPerformanceChart,
    PERFORMANCE_PERIOD_OPTIONS,
    type PerformancePeriod,
} from "~/modules/asset_screener/pages/listing/AssetPerformanceChart";
import type { PerformanceByPeriod } from "~/modules/asset_classes/performance-types";
import { getChartWatermarkClassName } from "~/modules/visualization/components/VisualizationCard/getChartWatermarkClassName";
import { formatDate } from "~/utils/functions/formatDate";

// // // //

const CHART_HEIGHT = 388;

/**
 * HomeAssetPerformanceCards
 * A single card pairing the asset class performance chart with the return
 * table. The table replaces the chart legend, so hovering a row highlights its
 * series and clicking a row hides or shows it. The period tabs re-scale the
 * chart only — the table sorts independently.
 */
export function HomeAssetPerformanceCards(props: {
    performanceByPeriod: PerformanceByPeriod;
}) {
    const { performanceByPeriod } = props;
    const [period, setPeriod] = useState<PerformancePeriod>("1Y");
    const chartData = performanceByPeriod[period];
    const [highlightedSeriesID, setHighlightedSeriesID] = useState<
        number | null
    >(null);
    const [hiddenSeriesIDs, setHiddenSeriesIDs] = useState<
        Record<number, boolean>
    >({});

    const toggleSeries = useCallback((seriesID: number) => {
        setHiddenSeriesIDs((current) => ({
            ...current,
            [seriesID]: !current[seriesID],
        }));
    }, []);

    // NOTE - a hidden series has no line to highlight, so treat it as no
    // highlight at all rather than dimming every other line for nothing
    const activeSeriesID =
        highlightedSeriesID !== null && hiddenSeriesIDs[highlightedSeriesID]
            ? null
            : highlightedSeriesID;

    return (
        <div className="col-span-12 min-w-0 overflow-clip rounded-lg border bg-card shadow-xs">
            <div className="flex flex-col items-start gap-2 px-5 pt-5 pb-4 md:flex-row md:items-center md:justify-between md:gap-4">
                <div className="flex min-w-0 flex-col items-start gap-x-2.5 select-none sm:flex-row sm:items-baseline">
                    <p className="text-xl leading-tight text-foreground">
                        Tokenized Asset Performance
                    </p>
                </div>
                <ButtonGroup
                    title={null}
                    value={period}
                    options={PERFORMANCE_PERIOD_OPTIONS}
                    onValueChange={(value) =>
                        setPeriod(value as PerformancePeriod)
                    }
                />
            </div>

            <div className="grid border-t border-border xl:grid-cols-[minmax(0,1fr)_auto]">
                <div className="min-w-0 pt-4 pr-2 pb-2.5 pl-1">
                    <AssetPerformanceChart
                        chartData={chartData}
                        className={getChartWatermarkClassName({
                            watermark: "center-40pct",
                        })}
                        group="assetClass"
                        height={CHART_HEIGHT}
                        hiddenSeriesIDs={hiddenSeriesIDs}
                        highlightedSeriesID={activeSeriesID}
                        onHighlightedSeriesChange={setHighlightedSeriesID}
                        period={period}
                        showLegend={false}
                    />
                </div>

                <AssetPerformanceLeaderboard
                    className="border-t border-border xl:border-t-0 xl:border-l"
                    group="assetClass"
                    hiddenSeriesIDs={hiddenSeriesIDs}
                    highlightedSeriesID={highlightedSeriesID}
                    onHighlightedSeriesChange={setHighlightedSeriesID}
                    onToggleSeries={toggleSeries}
                    performanceByPeriod={performanceByPeriod}
                    period={period}
                />
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-border px-5 pt-2 pb-2.5 select-none">
                <p className="text-xs text-muted-foreground">
                    Hover to highlight · click a row to hide or show its line
                </p>
                {chartData.asOf && (
                    <p className="text-xs font-light tracking-wide whitespace-nowrap text-muted-foreground">
                        as of {formatDate(new Date(chartData.asOf))}
                    </p>
                )}
            </div>
        </div>
    );
}
